// ...existing code...
export type AiDifyCapabilityType = 'chat' | 'workflow' | 'agent';

// ─── 输入字段定义（通用） ─────────────────────

/** 输入字段类型 */
export type InputFieldType = 'text' | 'number' | 'select' | 'hidden' | 'textarea';

/** 单个输入字段定义 */
export interface InputFieldDefinition {
  /** 字段键名，对应 Dify inputs 的 key，如 "table_id" */
  readonly key: string;
  /** 显示标签，如 "表格ID" */
  readonly label: string;
  /** 字段类型 */
  readonly type: InputFieldType;
  /** 是否必填 */
  readonly required: boolean;
  /** 默认值 */
  readonly defaultValue?: string;
  /** 占位提示 */
  readonly placeholder?: string;
  /** type=select 时的选项列表 */
  readonly options?: readonly string[];
  /** 字段描述说明 */
  readonly description?: string;
}

/** 默认空输入字段列表 */
export const DEFAULT_INPUT_FIELDS: readonly InputFieldDefinition[] = [];

/** 能力特性开关 */
export interface AiDifyCapabilityFeatures {
  /** 简易模式 */
  readonly simple_mode: boolean;
  /** 站内搜索 */
  readonly site_search: boolean;
  /** 增强模式 */
  readonly enhanced_mode: boolean;
  /** 技能/Skills */
  readonly skills: boolean;
}

/** 默认特性开关值 */
export const DEFAULT_CAPABILITY_FEATURES: AiDifyCapabilityFeatures = {
  simple_mode: false,
  site_search: false,
  enhanced_mode: false,
  skills: false,
};

/** 模式（pattern）特性键 → 中文标签映射 */
export const PATTERN_FEATURE_MAP: Readonly<Record<string, string>> = {
  simple_mode: '快速模式',
  enhanced_mode: '增强模式',
  site_search: '站内搜索',
} as const;

/** 可作为 pattern 参数的特性键 */
export type PatternFeatureKey = 'simple_mode' | 'site_search' | 'enhanced_mode';

// ─── 欢迎页配置 ─────────────────────

/** 单个提示词卡片定义 */
export interface WelcomePromptCard {
  /** 提示词唯一标识 */
  readonly id: string;
  /** 提示词文本内容 */
  readonly text: string;
  /** 图标 CSS 类名，如 "fas fa-code" */
  readonly iconClass: string;
}

/** 欢迎页配置 */
export interface WelcomeConfig {
  /** 大标题，如 "电气元件选型设计" */
  readonly title: string;
  /** 副标题/描述文字 */
  readonly subtitle: string;
  /** 提示词卡片列表 */
  readonly prompts: readonly WelcomePromptCard[];
}

/** 默认欢迎页配置 */
export const DEFAULT_WELCOME_CONFIG: WelcomeConfig = {
  title: '',
  subtitle: '',
  prompts: [],
};

export interface AiDifyCapability {
  readonly id: number;
  readonly code: string;
  readonly name: string;
  readonly type: AiDifyCapabilityType;
  readonly api_key: string;
  readonly endpoint: string;
  readonly enabled: boolean;
  readonly features?: AiDifyCapabilityFeatures;
  /** 自定义输入字段定义列表 */
  readonly input_fields?: readonly InputFieldDefinition[];
  /** 欢迎页配置 */
  readonly welcome_config?: WelcomeConfig;
  /** 输入框占位提示文本 */
  readonly placeholder?: string;

  readonly date_created?: string;
  readonly date_updated?: string;
  readonly user_created?: string | number | null;
  readonly user_updated?: string | number | null;
}

export interface AiDifyCapabilityCreatePayload {
  readonly code: string;
  readonly name: string;
  readonly type: AiDifyCapabilityType;
  readonly api_key: string;
  readonly endpoint: string;
  readonly enabled?: boolean;
  readonly features?: AiDifyCapabilityFeatures;
  /** 自定义输入字段定义列表 */
  readonly input_fields?: readonly InputFieldDefinition[];
  /** 欢迎页配置 */
  readonly welcome_config?: WelcomeConfig;
  /** 输入框占位提示文本 */
  readonly placeholder?: string;
}

export interface AiDifyCapabilityUpdatePayload {
  readonly code?: string;
  readonly name?: string;
  readonly type?: AiDifyCapabilityType;
  readonly api_key?: string;
  readonly endpoint?: string;
  readonly enabled?: boolean;
  readonly features?: AiDifyCapabilityFeatures;
  /** 自定义输入字段定义列表 */
  readonly input_fields?: readonly InputFieldDefinition[];
  /** 欢迎页配置 */
  readonly welcome_config?: WelcomeConfig;
  /** 输入框占位提示文本 */
  readonly placeholder?: string;
}

export interface DirectusListResponse<T> {
  readonly data: ReadonlyArray<T>;
}